/**
 * AUTHENTICATION HOOK
 * ===================
 *
 * Neon Auth (Managed Better Auth) state, via the Supabase-compatible adapter
 * in @neondatabase/neon-js.
 *
 * Auth state lives in one Context rather than per-caller hook state, for two
 * reasons. Every `useAuth()` caller would otherwise open its own adapter
 * subscription and fire its own /get-session request on mount (there are a
 * dozen callers). More importantly the copies would disagree: signing in
 * updates only the copy belonging to the component that called signIn, so the
 * rest of the app still reads `user === null` and treats the user as signed
 * out.
 */

import {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { client, invokeFunction, type User, type Session } from '@/integrations/neon/client';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

/**
 * The failure shape every adapter call returns, derived from the adapter
 * rather than imported from a nested package path (see client.ts). `null` is
 * part of it: that is the success case.
 */
type AuthError = Awaited<ReturnType<typeof client.auth.signInWithPassword>>['error'];

interface AuthMethods {
  signIn: (email: string, password: string) => Promise<{ error: AuthError }>;
  /**
   * `needsEmailVerification` means the account was created but no session was
   * issued because the project verifies at sign-up. Not an error state.
   */
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<{ error: AuthError; needsEmailVerification: boolean }>;
  /**
   * Confirm the code emailed at sign-up. `signedIn` reports whether the
   * provider also issued a session; when it did not, the account is verified
   * but the user still has to sign in.
   */
  verifyEmailOtp: (email: string, token: string) => Promise<{ error: AuthError; signedIn: boolean }>;
  /** Send a fresh sign-up verification code. */
  resendVerificationEmail: (email: string) => Promise<{ error: AuthError }>;
  signOut: () => Promise<{ error: AuthError }>;
  resetPassword: (email: string) => Promise<{ error: AuthError }>;
  resetPasswordWithToken: (newPassword: string, token: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError }>;
  /**
   * Change the signed-in user's password. Requires the current one, and
   * invalidates every other session on success.
   */
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ error: Error | null }>;
  /**
   * Change the signed-in user's email address.
   *
   * `verificationRequired` reports that the address has not changed yet: when
   * the current address is verified, the provider emails a confirmation link
   * to it and applies the change only once that link is followed. An
   * unverified address is replaced immediately.
   */
  changeEmail: (newEmail: string) => Promise<{ error: Error | null; verificationRequired: boolean }>;
}

function useAuthState(): AuthState & AuthMethods {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Re-read the session and publish it.
   *
   * Required after every auth transition this tab performs. The adapter
   * announces sign-in/sign-out by posting to a BroadcastChannel tagged with the
   * current tab's id, and its own listener ignores messages carrying that id
   * (supabase-adapter: `if (message.clientId === CURRENT_TAB_CLIENT_ID) return`).
   * So onAuthStateChange only ever fires for changes made in *other* tabs, plus
   * one INITIAL_SESSION when the listener is first attached. Without this the
   * tab that just signed in never learns that it did.
   */
  /**
   * The adapter deserializes a fresh `user` object on every auth event, token
   * refresh included. Storing it unconditionally changed the reference without
   * changing the identity, which re-ran every consumer effect keyed on `user` —
   * useProfile and useDecisions re-fetched in a loop, and useProfile's `loading`
   * flipped back to true each round, leaving the Settings save button disabled.
   * Keep the previous object whenever the fields consumers read are unchanged.
   */
  const applySession = useCallback((next: Session | null) => {
    setSession(next);
    setUser((prev) => {
      const nextUser = next?.user ?? null;
      if (!prev || !nextUser) return nextUser;
      if (
        prev.id === nextUser.id &&
        prev.email === nextUser.email &&
        prev.email_confirmed_at === nextUser.email_confirmed_at
      ) {
        return prev;
      }
      return nextUser;
    });
    setLoading(false);
  }, []);

  const refreshFromSession = useCallback(async () => {
    const { data } = await client.auth.getSession();
    const current = data?.session ?? null;
    applySession(current);
    return current;
  }, [applySession]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = client.auth.onAuthStateChange(
      (_event, session) => {
        applySession(session);
      }
    );

    // THEN check for existing session
    client.auth.getSession()
      .then(({ data: { session } }) => {
        applySession(session);
      })
      .catch(() => {
        // getSession rejects whenever the auth endpoint is unreachable. The
        // listener above normally settles `loading` first, so the point of
        // this handler is that the failure is observed rather than surfacing
        // as an unhandled promise rejection. Session state is left to the
        // listener, which knows more than "this request failed".
        setLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [applySession]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await client.auth.signInWithPassword({
      email,
      password,
    });
    if (!error) await refreshFromSession();
    return { error };
  }, [refreshFromSession]);

  const signUp = useCallback(async (email: string, password: string, firstName?: string, lastName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    const { data, error } = await client.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        // The Neon adapter spreads `data` directly into Better Auth's
        // signUp.email call, so every key here has to be a real Better Auth
        // user field. `name` is the only one that applies; an extra key such as
        // `full_name` is rejected as unknown unless Neon Auth declares it.
        data: { name: fullName || email.split('@')[0] },
      }
    });

    // With "Verify at Sign-up" enabled on the Neon Auth project, a successful
    // sign-up deliberately returns no session — the account exists and an
    // OTP/verification email goes out, but the user is not logged in yet. The
    // adapter reports that absent session as a `session_not_found` error, so
    // treating it as a failure would tell the user their sign-up failed while
    // the account was in fact created, leaving retries to fail as "user already
    // exists". Report it as what it is instead.
    if (error && (error as { code?: string }).code === 'session_not_found') {
      return { error: null, needsEmailVerification: true };
    }

    // If signup was successful and we have user data, add to Loops
    if (!error && data.user && (firstName || lastName)) {
      try {
        await invokeFunction('/add-to-loops', {
          body: { firstName, lastName }
        });
      } catch (loopsError) {
        console.warn('Failed to add user to Loops onboarding:', loopsError);
        // Don't fail the signup if Loops integration fails
      }
    }

    if (!error) await refreshFromSession();

    return { error, needsEmailVerification: false };
  }, [refreshFromSession]);

  const verifyEmailOtp = useCallback(async (email: string, token: string) => {
    // type 'signup' routes to Better Auth's emailOtp.verifyEmail. ('email'
    // would instead attempt a passwordless sign-in, which is a different flow.)
    const { data, error } = await client.auth.verifyOtp({
      email,
      token,
      type: 'signup',
    });
    // Verification succeeds whether or not a session comes back with it.
    const signedIn = !error && !!data?.session;
    if (signedIn) await refreshFromSession();
    return { error, signedIn };
  }, [refreshFromSession]);

  const resendVerificationEmail = useCallback(async (email: string) => {
    const { error } = await client.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/` },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await client.auth.signOut();
    // Clear locally even if the server call failed; the adapter has already
    // dropped its cached session.
    await refreshFromSession();
    return { error };
  }, [refreshFromSession]);

  const resetPassword = useCallback(async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    const { error } = await client.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  }, []);

  /**
   * Password and email changes do not go through the Supabase-compatible
   * surface: its `updateUser` rejects both fields and points at Better Auth's
   * own `changePassword`/`changeEmail`, which the adapter exposes through
   * `getBetterAuthInstance()`. Those return Better Auth's
   * `{ code?, message?, status, statusText }` failure shape rather than an
   * AuthError, so they are normalized to Error here.
   */
  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const { error } = await client.auth.getBetterAuthInstance().changePassword({
      currentPassword,
      newPassword,
      // A password change is usually a response to a suspected compromise, so
      // drop every other session rather than leaving them signed in.
      revokeOtherSessions: true,
    });

    if (error) {
      return { error: new Error(error.message || 'Failed to update password') };
    }

    // revokeOtherSessions rotates this tab's session token; re-read it so
    // subsequent Data API calls carry the current one.
    await refreshFromSession();
    return { error: null };
  }, [refreshFromSession]);

  const changeEmail = useCallback(async (newEmail: string) => {
    // Read before the call: once the change applies, the session carries the
    // new address and no longer says whether the old one was verified.
    const verificationRequired = !!user?.email_confirmed_at;

    const { error } = await client.auth.getBetterAuthInstance().changeEmail({
      newEmail,
      callbackURL: `${window.location.origin}/`,
    });

    if (error) {
      return {
        error: new Error(error.message || 'Failed to update email address'),
        verificationRequired: false,
      };
    }

    // Only meaningful when the address changed immediately; harmless otherwise.
    await refreshFromSession();
    return { error: null, verificationRequired };
  }, [refreshFromSession, user]);

  const resetPasswordWithToken = useCallback(async (newPassword: string, token: string) => {
    const { error } = await client.auth.getBetterAuthInstance().resetPassword({
      newPassword,
      token,
    });
    if (error) {
      return { error: new Error(error.message || 'Failed to reset password') };
    }
    return { error: null };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      },
    });
    return { error };
  }, []);

  return {
    user,
    session,
    loading,
    signIn,
    signUp,
    verifyEmailOtp,
    resendVerificationEmail,
    signOut,
    resetPassword,
    resetPasswordWithToken,
    signInWithGoogle,
    changePassword,
    changeEmail,
  };
}

// Auth state is shared through one provider so every consumer sees the same
// session and only one /get-session request is made per page load.
const AuthContext = createContext<(AuthState & AuthMethods) | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useAuthState();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider; only fast refresh is affected.
export function useAuth(): AuthState & AuthMethods {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
